import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { round2 } from '@/lib/pricing-math';
import type { DesignVariantPricing } from '@/api/designs';

interface VariantSurchargeListProps {
  /** The quote's variants of this product. */
  variants: DesignVariantPricing[];
  /** The product's gross selling price from the quote; null when unpriced. */
  sellingPrice: string | null;
  /** Colours the seller picked; an empty list means every colour counts. */
  selectedColors: string[];
  formatCurrency: (value: number) => string;
}

interface SurchargeRow {
  size: string;
  surcharge: number;
}

/**
 * The end-customer surcharges of one product: sizes whose blank costs more
 * than the blank reference price carry the difference on top of the selling
 * price, so the seller's profit stays the same on every variant (plan KTD4).
 * Shown is what the customer pays on top — the variant's gross price minus
 * the gross selling price; the quote's `surcharge` itself is net and only
 * stands in while the product has no price yet. Never the blank price.
 */
export function VariantSurchargeList({
  variants,
  sellingPrice,
  selectedColors,
  formatCurrency,
}: VariantSurchargeListProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const { rows, allAboveReference } = useMemo(() => {
    const relevant = variants.filter(
      (variant) =>
        variant.productColorId === null ||
        selectedColors.length === 0 ||
        selectedColors.includes(variant.productColorId)
    );
    const gross = sellingPrice === null ? null : Number(sellingPrice);
    const bySize = new Map<string, number>();
    for (const variant of relevant) {
      if (variant.surcharge === null) continue;
      const surcharge =
        gross !== null && variant.price !== null
          ? round2(Number(variant.price) - gross)
          : Number(variant.surcharge);
      if (!(surcharge > 0)) continue;
      const size = variant.sizeCode ?? variant.variantId;
      bySize.set(size, Math.max(bySize.get(size) ?? 0, surcharge));
    }
    const sorted: SurchargeRow[] = [...bySize.entries()]
      .map(([size, surcharge]) => ({ size, surcharge }))
      .sort(
        (a, b) => a.surcharge - b.surcharge || a.size.localeCompare(b.size)
      );
    // Every picked colour sits above the reference: the "surcharge" then
    // applies to each variant the seller offers, which the list should say.
    const above =
      relevant.length > 0 &&
      relevant.every(
        (variant) => variant.surcharge !== null && Number(variant.surcharge) > 0
      );
    return { rows: sorted, allAboveReference: above };
  }, [variants, sellingPrice, selectedColors]);

  return (
    <div className="dc-type-note mt-2 text-[var(--mute)]">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        className="flex w-full items-center justify-between gap-2 text-left hover:text-[var(--ink-2)]"
      >
        <span>
          {t('creator.productCard.surcharges', { count: rows.length })}
        </span>
        <ChevronDown
          className={cn('h-3 w-3 transition', isOpen && 'rotate-180')}
          aria-hidden="true"
        />
      </button>
      {isOpen && (
        <div className="mt-1.5 space-y-0.5">
          {rows.length === 0 ? (
            <p>{t('creator.productCard.noSurcharges')}</p>
          ) : (
            <ul className="space-y-0.5">
              {rows.map((row) => (
                <li
                  key={row.size}
                  className="flex justify-between tabular-nums"
                >
                  <span className="text-[var(--ink-2)]">{row.size}</span>
                  <span>
                    {t('creator.productCard.surchargeRow', {
                      amount: formatCurrency(row.surcharge),
                    })}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {allAboveReference && (
            <p className="pt-1 text-[var(--warning,var(--mute))]">
              {t('creator.productCard.allAboveReference')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
